import {
	DEFAULT_PRICE_IMPACT_DELTA_PERCENT,
	MIN_MARGIN_AMOUNT_V3,
	PERIOD_IN_SECONDS,
	SL_TP_MAX_SIZE,
	SNX_V3_PERPS_ADDRESSES,
	TOKEN_ADDRESSES,
} from '@kwenta/sdk/constants'
import {
	type ConditionalOrderV2,
	type ConditionalOrderV3Response,
	type DelegateChangeInfo,
	FuturesMarginType,
	type FuturesMarketAsset,
	type FuturesTrade,
	type FuturesVolumes,
	type NetworkId,
	OrderTypeEnum,
	type Period,
	PerpsProvider,
	type PerpsV2Position,
	type PerpsV3Position,
	type PnlSnapshot,
	PositionSide,
	PotentialTradeStatus,
	type SmartMarginOrderInputs,
	type SnxV2NetworkIds,
	type SnxV3NetworkIds,
	TransactionStatus,
	type WriteContractParameters,
} from '@kwenta/sdk/types'
import {
	calculateDesiredFillPrice,
	floorNumber,
	getDefaultPriceImpact,
	getTradeStatusMessage,
	simulatedRequestToTxRequest,
	tokenDecimals,
} from '@kwenta/sdk/utils'
import type Wei from '@kwenta/wei'
import { wei } from '@kwenta/wei'
import { createAsyncThunk } from '@reduxjs/toolkit'
import cloneDeep from 'lodash/cloneDeep'
import debounce from 'lodash/debounce'
import { AbstractionToken, type Transaction } from 'types/accountAbstraction'
import type { ConditionOrderTableItem } from 'types/futures'
import {
	type Address,
	type Hash,
	type WalletClient,
	getAddress,
	isHash,
	parseEther,
	parseUnits,
	zeroAddress,
} from 'viem'

import { IS_ONE_CLICK_TRADING_ENABLED } from 'constants/defaults'
import { monitorAndAwaitTransaction, monitorAndAwaitUserOp } from 'state/app/helpers'
import {
	handleTransactionError,
	setOpenModal,
	setOpenModalWithParams,
	setShowEditPositionModal,
	setTransaction,
	updateTransactionStatus,
} from 'state/app/reducer'
import { fetchBalancesAndAllowances } from 'state/balances/actions'
import { ZERO_STATE_TRADE_INPUTS } from 'state/constants'
import {
	clearCrossMarginTradeInputs,
	editCloseCMPositionPrice,
	editCrossMarginCloseAmount,
	editCrossMarginConditionalOrder,
	editCrossMarginPositionOrderPrice,
	editCrossMarginPositionSize,
	editCrossMarginTradeOrderPrice,
	editCrossMarginTradeSize,
	fetchAccountCollateralBalances,
	fetchMarginSnapshotsV3,
	fetchPerpsV3Balances,
	fetchSupportedCollaterals,
	prepareCrossMarginTradeTx,
	removeStaleOrders,
} from 'state/futures/snxPerpsV3/actions'
import { handleFetchError } from 'state/helpers'
import type { AppThunk } from 'state/store'
import { FetchStatus, type ThunkConfig } from 'state/types'
import {
	selectAbstractionWalletEnabled,
	selectAccountAbstractionOwnerAddress,
	selectOneClickTradingWalletAddress,
	selectSignerWallet,
	selectWallet,
} from 'state/wallet/selectors'
import type { DateInterval } from 'utils/dates'
import { currentTimestamp } from 'utils/dates'
import {
	formatDelayedOrders,
	orderPriceInvalidLabel,
	providerIsCrossMargin,
	serializeFuturesVolumes,
	serializeIsolatedMarginTradePreview,
	serializePosition,
	serializeTrades,
	serializeV2Markets,
	serializeV3AsyncOrder,
	serializeV3Market,
} from 'utils/futures'
import logError from 'utils/logError'

import { notifyError } from 'components/ErrorNotifier'
import type { TransactionOrder } from 'state/app/types'
import {
	fetchAccountMarginInfo,
	submitFuturesTransaction,
	submitTransaction,
} from './common/actions'
import {
	selectAccountData,
	selectEditPositionInputs,
	selectIsConditionalOrder,
	selectLeverageSide,
	selectMarketAsset,
	selectMarketIndexPrice,
	selectMarketInfo,
	selectPerpsProvider,
	selectPriceImpact,
	selectProviderNetworks,
	selectSkewAdjustedPriceInfo,
	selectSnxPerpsV2Network,
	selectSnxV3Networks,
	selectTradeOrderType,
	selectTradePanelInputs,
	selectTradePanelOrderPriceInput,
	selectTradePanelSlTpInputs,
	selectTradePanelTradePrice,
	selectTradingMode,
	selectV2Markets,
	selectV3Markets,
} from './common/selectors'
import type {
	DebouncedPreviewParams,
	FundingRates,
	IsolatedTradePreviewParams,
	SnxPerpsV3TradePreviewParams,
} from './common/types'
import {
	calculateSnxV2TradeFees,
	clearSmartMarginTradeInputs,
	editCloseIsolatedPositionPrice,
	editCloseIsolatedPositionSizeDelta,
	editIsolatedMarginConditionalOrder,
	editIsolatedMarginPositionOrderPrice,
	editIsolatedMarginPositionSize,
	editSmartMarginTradeOrderPrice,
} from './isolatedMargin/actions'
import {
	selectIsolatedAvailableMargin,
	selectIsolatedMarginDelayedOrders,
	selectIsolatedMarginMarginDelta,
	selectOldestIsolatedGlobalTrade,
	selectSnxV2Account,
} from './isolatedMargin/selectors'

import { setOneClickTradingWalletAddress } from 'state/wallet/reducer'
import {
	addSavedAddress as addAddress,
	fetchSavedAddresses as fetchAddresses,
	removeSavedAddress as removeAddress,
} from 'utils/auth'
import {
	addDelegateToAddressBook,
	clearEditPositionInputs,
	clearTradePreview,
	handlePreviewError,
	incrementPreviewCount,
	removeDelegateFromAddressBook,
	setAccount,
	setCancellingConditionalOrder,
	setDelegated,
	setEditConditonalOrderInputs,
	setEditPositionInputs,
	setFundingRatesHistory,
	setGlobalTradeHistory,
	setLeverageInput,
	setLeverageSide,
	setMarginDelta,
	setMarkets,
	setQueryStatus,
	setSelectedMarketAsset,
	setSession,
	setSubAccountsForWallet,
	setTradeInputs,
	setTradePreview,
	setTradeStopLoss,
	setTraderHistory,
	setVolumes,
	updateAccountData,
} from './reducer'
import {
	selectAbstractionDelegated,
	selectAccountContext,
	selectAccountContexts,
	selectAddressBook,
	selectAuthToken,
	selectEditPositionModalInfo,
	selectNewNickname,
	selectPosition,
	selectPreviewCount,
	selectQueryStatuses,
	selectSelectedDelegationAddress,
	selectSelectedTrader,
	selectSessionExpiry,
	selectSubmittingFuturesTx,
	selectTradePreview,
} from './selectors'
import {
	selectAsyncCrossMarginOrder,
	selectCrossMarginAssetsForPrices,
	selectCrossMarginAvailableMargin,
	selectCrossMarginMarginInfo,
	selectMargineEngineEnabled,
	selectReservedMarginForOrders,
	selectSnxV3Account,
	selectSnxV3AccountContext,
	selectSnxV3GlobalTradesForMarket,
} from './snxPerpsV3/selectors'
import type { SnxPerpsV3TradePreview } from './snxPerpsV3/types'
import { type DelegationAccountInfo, ManageModalType, TradingModes } from './types'

export const setMarketAsset = createAsyncThunk<void, FuturesMarketAsset, ThunkConfig>(
	'futures/setMarketAsset',
	async (asset, { dispatch, getState }) => {
		const provider = selectPerpsProvider(getState())
		dispatch(clearTradeInputs())
		dispatch(setSelectedMarketAsset({ asset, provider }))
	}
)

export const fetchMarkets = createAsyncThunk<void, { providers: PerpsProvider[] }, ThunkConfig>(
	'futures/fetchMarkets',
	async ({ providers }, { dispatch, getState, extra: { sdk } }) => {
		for (const p of providers) {
			dispatch(setQueryStatus({ key: 'get_markets', status: FetchStatus.Loading, provider: p }))
			try {
				if (p === PerpsProvider.SNX_V2_OP) {
					const v2Network = selectSnxPerpsV2Network(getState())
					const markets = await sdk.snxPerpsV2.getMarkets(v2Network)
					const serializedMarkets = serializeV2Markets(markets)
					dispatch(
						setMarkets({
							provider: PerpsProvider.SNX_V2_OP,
							markets: serializedMarkets,
						})
					)
				}

				if (providerIsCrossMargin(p)) {
					const v3Networks = selectSnxV3Networks(getState())
					const v3Markets = await sdk.snxPerpsV3.getMarkets(v3Networks[p])
					dispatch(
						setMarkets({
							markets: v3Markets.map(serializeV3Market),
							provider: p,
						})
					)
					dispatch(fetchSupportedCollaterals())
				}

				dispatch(setQueryStatus({ key: 'get_markets', status: FetchStatus.Success }))
			} catch (err) {
				handleFetchError(dispatch, 'get_markets', err, { provider: p })
			}
		}
	}
)

export const fetchDailyVolumes = createAsyncThunk<
	void,
	{ providers: PerpsProvider[] },
	ThunkConfig
>('futures/fetchDailyVolumes', async ({ providers }, { getState, dispatch, extra: { sdk } }) => {
	for (const p of providers) {
		dispatch(setQueryStatus({ key: 'get_daily_volumes', status: FetchStatus.Loading, provider: p }))
		const chain = selectProviderNetworks(getState())[p]
		try {
			let volumes: FuturesVolumes = {}
			switch (p) {
				case PerpsProvider.SNX_V2_OP:
					volumes = await sdk.snxPerpsV2.getDailyVolumes(chain as SnxV2NetworkIds)
					break
				case PerpsProvider.SNX_V3_BASE:
					volumes = await sdk.snxPerpsV3.getDailyVolumes(chain as SnxV3NetworkIds)
					break
			}

			const serialized = serializeFuturesVolumes(volumes)
			dispatch(
				setVolumes({
					provider: p,
					volumes: serialized,
				})
			)
			dispatch(
				setQueryStatus({ key: 'get_daily_volumes', status: FetchStatus.Success, provider: p })
			)
		} catch (err) {
			handleFetchError(dispatch, 'get_daily_volumes', err, { provider: p })
		}
	}
})

export const fetchMarketData = createAsyncThunk<void, PerpsProvider[], ThunkConfig>(
	'futures/fetchMarketData',
	async (providers, { dispatch }) => {
		await dispatch(fetchMarkets({ providers }))
		dispatch(fetchDailyVolumes({ providers }))
	}
)

export const fetchFundingRatesHistory = createAsyncThunk<
	void,
	{ marketAsset: FuturesMarketAsset; period: Period; provider: PerpsProvider }[],
	ThunkConfig
>('futures/fetchFundingRatesHistory', async (providers, { dispatch, getState, extra: { sdk } }) => {
	for (const p of providers) {
		const chain = selectProviderNetworks(getState())[p.provider]

		try {
			dispatch(
				setQueryStatus({
					key: 'get_funding_history',
					status: FetchStatus.Loading,
					provider: p.provider,
				})
			)
			let rates: FundingRates = []
			switch (p.provider) {
				case PerpsProvider.SNX_V2_OP:
					rates = await sdk.snxPerpsV2.getMarketFundingRatesHistory(
						p.marketAsset,
						PERIOD_IN_SECONDS[p.period]
					)
					break
				case PerpsProvider.SNX_V3_BASE:
					rates = await sdk.snxPerpsV3.getMarketFundingRatesHistory(
						p.marketAsset,
						chain as SnxV3NetworkIds,
						PERIOD_IN_SECONDS[p.period]
					)
					break
			}

			dispatch(setFundingRatesHistory({ asset: p.marketAsset, rates, provider: p.provider }))

			dispatch(
				setQueryStatus({
					key: 'get_funding_history',
					status: FetchStatus.Success,
					provider: p.provider,
				})
			)
		} catch (err) {
			handleFetchError(dispatch, 'get_funding_history', err, { provider: p.provider })
		}
	}
})

export const fetchActivePositions = createAsyncThunk<void, PerpsProvider[], ThunkConfig>(
	'futures/fetchActivePositions',
	async (providers, { dispatch, getState, extra: { sdk } }) => {
		const contexts = selectAccountContexts(getState())

		for (const provider of providers) {
			try {
				const { wallet, network, accountId } = contexts[provider]
				if (!wallet || !accountId) continue

				dispatch(
					setQueryStatus({ key: 'get_positions', status: FetchStatus.Loading, provider: provider })
				)

				if (provider === PerpsProvider.SNX_V2_OP) {
					const markets = selectV2Markets(getState())
					const positions = await sdk.snxPerpsV2.getPositions({
						account: accountId,
						futuresMarkets: markets.map((m) => ({
							asset: m.asset,
							address: m.marketAddress,
						})),
						chainId: network as SnxV2NetworkIds,
						status: 'open',
					})
					const serializedPositions = positions.map(serializePosition)
					dispatch(
						updateAccountData({
							wallet,
							data: {
								provider: PerpsProvider.SNX_V2_OP,
								positions: serializedPositions,
								network: network as NetworkId,
								account: accountId,
							},
						})
					)
				} else if (providerIsCrossMargin(provider)) {
					const assetsForPrices = selectCrossMarginAssetsForPrices(getState())
					const marginInfo = selectCrossMarginMarginInfo(getState())
					const markets = selectV3Markets(getState())
					const positions = await sdk.snxPerpsV3.getPositions({
						accountId,
						futuresMarkets: markets.map((m) => ({
							id: BigInt(m.marketId),
							asset: m.asset,
						})),
						assetsForPrices,
						chainId: network as SnxV3NetworkIds,
						status: 'open',
						marginInfo: {
							availableMargin: wei(marginInfo.availableMargin),
							requiredMaintenanceMargin: wei(marginInfo.requiredMaintenanceMargin),
						},
					})

					const serializedPositions = positions.map(serializePosition)

					dispatch(
						updateAccountData({
							wallet,
							data: {
								network: network,
								account: accountId.toString(),
								positions: serializedPositions,
								provider: provider,
							},
						})
					)
				}
				dispatch(setQueryStatus({ key: 'get_positions', status: FetchStatus.Success, provider }))
			} catch (err) {
				handleFetchError(dispatch, 'get_positions', err, { provider })
			}
		}
	}
)

export const fetchOpenConditionalOrders = createAsyncThunk<void, PerpsProvider[], ThunkConfig>(
	'futures/fetchOpenConditionalOrders',
	async (providers, { dispatch, getState, extra: { sdk } }) => {
		const contexts = selectAccountContexts(getState())
		let orders: ConditionalOrderV3Response[] | ConditionalOrderV2[] = []
		for (const provider of providers) {
			try {
				const { network: networkId, accountId, wallet } = contexts[provider]
				if (!accountId || !networkId || !wallet) continue

				dispatch(
					setQueryStatus({ key: 'get_conditional_orders', status: FetchStatus.Loading, provider })
				)
				if (providerIsCrossMargin(provider)) {
					orders = await sdk.snxPerpsV3.getConditionalOrders(
						BigInt(accountId),
						networkId as SnxV3NetworkIds
					)
					dispatch(
						updateAccountData({
							wallet,
							data: {
								network: networkId,
								provider,
								account: accountId.toString(),
								conditionalOrders: orders,
							},
						})
					)
				} else if (provider === PerpsProvider.SNX_V2_OP) {
					const markets = selectV2Markets(getState())

					const marketAddresses = markets.map((market) => market.marketAddress)
					const orders = await sdk.snxPerpsV2.getConditionalOrders(
						accountId,
						networkId as SnxV2NetworkIds
					)

					const delayedOrders = await sdk.snxPerpsV2.getDelayedOrders(
						accountId,
						marketAddresses,
						networkId as SnxV2NetworkIds
					)
					const nonzeroOrders = formatDelayedOrders(delayedOrders, markets)

					dispatch(
						updateAccountData({
							wallet,
							data: {
								provider: PerpsProvider.SNX_V2_OP,
								account: accountId,
								network: networkId,
								delayedOrders: nonzeroOrders,
								conditionalOrders: orders,
							},
						})
					)
				}

				dispatch(
					setQueryStatus({ key: 'get_conditional_orders', status: FetchStatus.Success, provider })
				)
			} catch (err) {
				handleFetchError(dispatch, 'get_conditional_orders', err, { provider })
			}
		}
	}
)

export const fetchPendingMarketOrders = createAsyncThunk<void, PerpsProvider[], ThunkConfig>(
	'futures/fetchPendingMarketOrders',
	async (providers, { dispatch, getState, extra: { sdk } }) => {
		const contexts = selectAccountContexts(getState())

		for (const provider of providers) {
			try {
				const { network: networkId, accountId, wallet } = contexts[provider]
				if (!accountId || !networkId || !wallet) continue

				dispatch(
					setQueryStatus({
						key: 'get_pending_market_orders',
						status: FetchStatus.Loading,
						provider,
					})
				)
				if (providerIsCrossMargin(provider)) {
					const existingOrder = selectAsyncCrossMarginOrder(getState())
					const order = await sdk.snxPerpsV3.getPendingAsyncOrder(
						BigInt(accountId),
						networkId as SnxV3NetworkIds
					)
					const orderChanged = existingOrder?.executableStartTime !== order?.settlementTime
					if (orderChanged) {
						dispatch(fetchActivePositions([provider]))
						dispatch(removeStaleOrders())
					}
					dispatch(
						updateAccountData({
							wallet,
							data: {
								account: accountId.toString(),
								provider,
								network: networkId,
								pendingAsyncOrder: order ? serializeV3AsyncOrder(order) : undefined,
							},
						})
					)
				} else if (provider === PerpsProvider.SNX_V2_OP) {
					const markets = selectV2Markets(getState())
					const marketAddresses = markets.map((market) => market.marketAddress)
					const delayedOrders = await sdk.snxPerpsV2.getDelayedOrders(
						accountId,
						marketAddresses,
						networkId as SnxV2NetworkIds
					)
					const nonzeroOrders = formatDelayedOrders(delayedOrders, markets)
					dispatch(
						updateAccountData({
							wallet,
							data: {
								provider: PerpsProvider.SNX_V2_OP,
								account: accountId,
								network: networkId,
								delayedOrders: nonzeroOrders,
							},
						})
					)
				}

				dispatch(
					setQueryStatus({
						key: 'get_pending_market_orders',
						status: FetchStatus.Success,
						provider,
					})
				)
			} catch (err) {
				handleFetchError(dispatch, 'get_pending_market_orders', err, { provider })
			}
		}
	}
)

export const fetchAllOpenOrders = createAsyncThunk<void, PerpsProvider[], ThunkConfig>(
	'futures/fetchAllOpenOrders',
	async (providers, { dispatch }) => {
		dispatch(fetchPendingMarketOrders(providers))
		dispatch(fetchOpenConditionalOrders(providers))
	}
)

export const fetchPerpsAccounts = createAsyncThunk<
	void,
	{ providers: PerpsProvider[]; refetch?: boolean },
	ThunkConfig
>(
	'futures/fetchPerpsAccounts',
	async ({ providers, refetch }, { getState, dispatch, extra: { sdk } }) => {
		const wallet = selectWallet(getState())
		const chainIds = selectProviderNetworks(getState())

		if (!wallet) return undefined

		for (const provider of providers) {
			dispatch(setQueryStatus({ key: 'get_accounts', status: FetchStatus.Loading, provider }))

			try {
				switch (provider) {
					case PerpsProvider.SNX_V2_OP: {
						const v2Network = selectSnxPerpsV2Network(getState())
						const v2Accounts = getState().futures.accounts[PerpsProvider.SNX_V2_OP]

						// Already have an account fetched and persisted for this address
						if (v2Accounts?.[wallet]?.network === v2Network) return

						const fetchedAccounts = await sdk.snxPerpsV2.getSmartMarginAccounts(
							wallet,
							chainIds[provider] as SnxV2NetworkIds
						)
						const account = fetchedAccounts[0]
						if (account) {
							dispatch(
								setAccount({
									account,
									wallet,
									networkId: v2Network,
									provider: PerpsProvider.SNX_V2_OP,
								})
							)
						}

						break
					}

					case PerpsProvider.SNX_V3_BASE: {
						const marginEngineEnabled = selectMargineEngineEnabled(getState())
						const v3Network = selectSnxV3Networks(getState())[provider]
						const existsing = getState().futures.accounts[provider]?.[wallet]
						const existingAccount = wallet
							? existsing?.network === v3Network && existsing.marginEnginePermitted
							: undefined

						if (existingAccount && !refetch) return

						dispatch(setQueryStatus({ key: 'get_accounts', status: FetchStatus.Loading }))

						const accounts = await sdk.snxPerpsV3.getAccounts(
							wallet,
							marginEngineEnabled,
							v3Network
						)

						if (!accounts.length) return undefined
						// TODO: Support multiple accounts
						const { accountId, marginEnginePermitted } = accounts[0]
						dispatch(
							setAccount({
								account: accountId.toString(),
								networkId: v3Network,
								wallet,
								provider,
								marginEnginePermitted,
							})
						)
						break
					}
				}
				dispatch(setQueryStatus({ key: 'get_accounts', status: FetchStatus.Success, provider }))
			} catch (err) {
				handleFetchError(dispatch, 'get_accounts', err, { provider })
			}
		}
	}
)

export const fetchHighFrequencyAccountData = createAsyncThunk<void, PerpsProvider[], ThunkConfig>(
	'futures/fetchHighFrequencyAccountData',
	async (providers, { dispatch }) => {
		dispatch(fetchActivePositions(providers))
		dispatch(fetchAllOpenOrders(providers))
		dispatch(fetchAccountMarginInfo(providers))

		for (const provider of providers) {
			if (providerIsCrossMargin(provider)) {
				dispatch(fetchAccountCollateralBalances())
			}
		}
	}
)

export const fetchLowFrequencyAccountData = createAsyncThunk<void, PerpsProvider[], ThunkConfig>(
	'futures/fetchLowFrequencyAccountData',
	async (providers, { dispatch }) => {
		dispatch(fetchMarginTransfers(providers))

		for (const provider of providers) {
			if (providerIsCrossMargin(provider)) {
				dispatch(fetchPerpsV3Balances())
			}
		}
	}
)

export const fetchAccountData = createAsyncThunk<void, PerpsProvider[], ThunkConfig>(
	'futures/fetchAccountData',
	async (providers, { dispatch }) => {
		await dispatch(fetchHighFrequencyAccountData(providers))
		await dispatch(fetchLowFrequencyAccountData(providers))
	}
)

export const fetchMarginTransfers = createAsyncThunk<void, PerpsProvider[], ThunkConfig>(
	'futures/fetchMarginTransfers',
	async (providers, { dispatch, getState, extra: { sdk } }) => {
		const wallet = selectWallet(getState())
		const contexts = selectAccountContexts(getState())

		if (!wallet) return

		for (const provider of providers) {
			try {
				const { network, accountId, wallet } = contexts[provider]
				if (!accountId || !network || !wallet) continue

				dispatch(
					setQueryStatus({ key: 'get_margin_transfers', status: FetchStatus.Loading, provider })
				)
				switch (provider) {
					case PerpsProvider.SNX_V2_OP: {
						const marketTransfers = await sdk.snxPerpsV2.getMarketMarginTransfers(
							accountId as Address,
							network as SnxV2NetworkIds
						)
						const accountTransfers = await sdk.snxPerpsV2.getSmartMarginAccountTransfers(
							accountId,
							network as SnxV2NetworkIds
						)

						dispatch(
							updateAccountData({
								wallet,
								data: {
									account: accountId,
									provider: PerpsProvider.SNX_V2_OP,
									network,
									marketMarginTransfers: marketTransfers,
									accountTransfers: accountTransfers,
								},
							})
						)
						break
					}

					case PerpsProvider.SNX_V3_BASE: {
						const accountTransfersV3 = await sdk.snxPerpsV3.getCollateralTransfers(
							network as SnxV3NetworkIds,
							BigInt(accountId)
						)
						dispatch(
							updateAccountData({
								wallet,
								data: {
									provider,
									account: accountId.toString(),
									network,
									accountTransfers: accountTransfersV3,
								},
							})
						)
						break
					}
				}
				dispatch(
					setQueryStatus({ key: 'get_margin_transfers', status: FetchStatus.Success, provider })
				)
			} catch (err) {
				handleFetchError(dispatch, 'get_margin_transfers', err, { provider })
			}
		}
	}
)

export const fetchOrderHistory = createAsyncThunk<void, PerpsProvider[], ThunkConfig>(
	'futures/fetchOrderHistory',
	async (providers, { getState, dispatch, extra: { sdk } }) => {
		const contexts = selectAccountContexts(getState())

		for (const provider of providers) {
			const { network, accountId, wallet } = contexts[provider]
			if (!accountId || !network || !wallet) continue

			dispatch(setQueryStatus({ key: 'get_order_history', status: FetchStatus.Loading, provider }))

			try {
				if (providerIsCrossMargin(provider)) {
					const orders = await sdk.snxPerpsV3.getOrderHistory(
						BigInt(accountId),
						network as SnxV3NetworkIds
					)
					dispatch(
						updateAccountData({
							wallet,
							data: {
								account: accountId,
								orderHistory: orders,
								network,
								provider,
							},
						})
					)
				} else if (provider === PerpsProvider.SNX_V2_OP) {
					const orderHistory = await sdk.snxPerpsV2.getOrderHistory(
						wallet,
						network as SnxV2NetworkIds
					)
					dispatch(
						updateAccountData({
							wallet,
							data: {
								network,
								account: accountId,
								provider: PerpsProvider.SNX_V2_OP,
								orderHistory,
							},
						})
					)
				}
				dispatch(
					setQueryStatus({ key: 'get_order_history', status: FetchStatus.Success, provider })
				)
			} catch (err) {
				handleFetchError(dispatch, 'get_order_history', err, { provider })
			}
		}
	}
)

export const fetchTradeHistory = createAsyncThunk<
	void,
	{
		providers: PerpsProvider[]
		pageSize?: number
		minTimestamp?: number
	},
	ThunkConfig
>(
	'futures/fetchTradeHistory',
	async ({ providers, pageSize = 50, minTimestamp }, { getState, dispatch, extra: { sdk } }) => {
		const contexts = selectAccountContexts(getState())

		for (const provider of providers) {
			const { network, accountId, wallet } = contexts[provider]
			if (!accountId || !network || !wallet) continue

			dispatch(setQueryStatus({ key: 'get_trade_history', status: FetchStatus.Loading, provider }))

			try {
				let trades: FuturesTrade[] = []
				if (providerIsCrossMargin(provider)) {
					trades = await sdk.snxPerpsV3.getTradeHistory(
						BigInt(accountId),
						network as SnxV3NetworkIds
					)
				} else if (provider === PerpsProvider.SNX_V2_OP) {
					trades = await sdk.snxPerpsV2.getTradeHistory({
						walletAddress: wallet,
						pageLength: pageSize,
						chainId: network as SnxV2NetworkIds,
						minTimestamp,
					})
				}
				dispatch(
					updateAccountData({
						wallet,
						data: {
							account: accountId,
							trades: serializeTrades(trades),
							network,
							provider,
						},
					})
				)

				dispatch(
					setQueryStatus({ key: 'get_trade_history', status: FetchStatus.Success, provider })
				)
			} catch (err) {
				handleFetchError(dispatch, 'get_trade_history', err, { provider })
			}
		}
	}
)

export const fetchGlobalTradeHistory = createAsyncThunk<
	void,
	{ providers: PerpsProvider[]; pageSize?: number; nextPage?: boolean },
	ThunkConfig
>(
	'futures/fetchGlobalTradeHistory',
	async ({ providers, pageSize = 50, nextPage }, { getState, dispatch, extra: { sdk } }) => {
		const contexts = selectAccountContexts(getState())
		const marketAsset = selectMarketAsset(getState())
		for (const provider of providers) {
			dispatch(setQueryStatus({ key: 'get_global_trades', status: FetchStatus.Loading, provider }))

			const { network } = contexts[provider]
			if (!network) continue

			try {
				let trades: FuturesTrade[] = []
				if (providerIsCrossMargin(provider)) {
					const existingTrades = selectSnxV3GlobalTradesForMarket(getState())

					const oldestTrade = existingTrades?.[existingTrades.length - 1]
					const maxTimestamp = nextPage
						? oldestTrade?.timestamp ?? currentTimestamp()
						: currentTimestamp()
					const minTimestamp = 0

					trades = await sdk.snxPerpsV3.getAllTradesByMarket(
						marketAsset,
						network as SnxV3NetworkIds,
						{
							minTimestamp,
							maxTimestamp,
							pageSize: 50,
						}
					)
				} else {
					const oldestTrade = selectOldestIsolatedGlobalTrade(getState())
					if (provider === PerpsProvider.SNX_V2_OP) {
						trades = await sdk.snxPerpsV2.getTradeHistory({
							pageLength: pageSize,
							marketAsset,
							maxTimestamp: nextPage ? oldestTrade?.timestamp : undefined,
							chainId: network as SnxV2NetworkIds,
						})
					}
				}

				dispatch(
					setGlobalTradeHistory({
						trades: serializeTrades(trades),
						provider,
						marketAsset,
					})
				)
				dispatch(
					setQueryStatus({ key: 'get_global_trades', status: FetchStatus.Success, provider })
				)
			} catch (err) {
				handleFetchError(dispatch, 'get_global_trades', err, { provider })
			}
		}
	}
)

const fetchPnlSnapshots = createAsyncThunk<void, void, ThunkConfig>(
	'futures/fetchPnlSnapshots',
	async (_, { dispatch, getState, extra: { sdk } }) => {
		const { network: networkId, accountId, wallet, provider } = selectAccountContext(getState())
		if (!wallet || !accountId) return

		try {
			if (provider === PerpsProvider.SNX_V2_OP) return
			dispatch(setQueryStatus({ key: 'get_pnl_snapshots', status: FetchStatus.Loading }))

			let snapshots: PnlSnapshot[] = []
			if (providerIsCrossMargin(provider)) {
				snapshots = await sdk.snxPerpsV3.getPnlSnapshots(
					BigInt(accountId),
					networkId as SnxV3NetworkIds
				)
			}
			const serialized = snapshots.map((s) => ({
				...s,
				pnl: s.pnl.toString(),
			}))
			dispatch(
				updateAccountData({
					wallet,
					data: {
						network: networkId,
						provider,
						account: accountId.toString(),
						pnlSnapshots: serialized,
					},
				})
			)
			dispatch(setQueryStatus({ key: 'get_pnl_snapshots', status: FetchStatus.Success }))
		} catch (err) {
			handleFetchError(dispatch, 'get_pnl_snapshots', err)
		}
	}
)

export const fetchDashboardChartData = (): AppThunk => (dispatch) => {
	dispatch(fetchMarginSnapshotsV3())
	dispatch(fetchPnlSnapshots())
}

const debouncedPrepareTradePreview = debounce((dispatch, inputs: DebouncedPreviewParams) => {
	dispatch(fetchTradePreview(inputs))
}, 500)

export const refetchTradePreview = (): AppThunk => (dispatch, getState) => {
	const { accountId } = selectAccountContext(getState())
	const tradeIndexPrice = selectMarketIndexPrice(getState())
	const tradeMarketInfo = selectMarketInfo(getState())
	const tradeMarketPrice = selectSkewAdjustedPriceInfo(getState())
	const preview = selectTradePreview(getState())
	const tradePending = selectSubmittingFuturesTx(getState())

	if (!accountId) throw new Error('No account selected')

	if (!preview || tradePending) return

	const {
		market: editMarket,
		indexPrice: editIndexPrice,
		marketPrice: editMarketPrice,
	} = selectEditPositionModalInfo(getState())

	const marketInfo = preview.action === 'trade' ? tradeMarketInfo : editMarket
	const indexPrice = preview.action === 'trade' ? wei(tradeIndexPrice) : wei(editIndexPrice)
	const marketPrice = preview.action === 'trade' ? wei(tradeMarketPrice ?? 0) : wei(editMarketPrice)

	if (!marketInfo) throw new Error('No market selected')

	const queryKey = `get_trade_preview_${preview?.action}`
	const isFetching = selectQueryStatuses(getState())[queryKey]?.status === FetchStatus.Loading

	if (isFetching) return

	dispatch(
		stageTradePreview({
			indexPrice: indexPrice,
			provider: marketInfo.provider,
			accountId,
			market: marketInfo,
			orderPrice: wei(preview.orderPrice),
			marketPrice: wei(marketPrice ?? 0),
			marginDelta: wei(preview.marginDelta ?? 0),
			sizeDelta: wei(preview.sizeDelta),
			action: preview.action,
			isConditional: preview.isConditional,
		} as IsolatedTradePreviewParams | SnxPerpsV3TradePreviewParams)
	)
}

export const stageTradePreview = createAsyncThunk<
	void,
	IsolatedTradePreviewParams | SnxPerpsV3TradePreviewParams,
	ThunkConfig
>('futures/stageTradePreview', async (inputs, { dispatch, getState }) => {
	dispatch(incrementPreviewCount())
	const debounceCount = selectPreviewCount(getState())
	debouncedPrepareTradePreview(dispatch, { ...inputs, debounceCount })
})

const fetchTradePreview = createAsyncThunk<void, DebouncedPreviewParams, ThunkConfig>(
	'futures/fetchTradePreview',
	async (params, { dispatch, getState, extra: { sdk } }) => {
		const accountData = selectAccountData(getState())
		const slippage = selectPriceImpact(getState())

		const positions = accountData?.positions ?? []
		const position = positions.find((p) => p.asset === params.market.asset)

		const queryKey = `get_trade_preview_${params.action}`
		const orderPrice = params.orderPrice ?? params.indexPrice

		// Check the preview hasn't been cleared before query resolves
		const count = selectPreviewCount(getState())

		if (count !== params.debounceCount) {
			const existing = getState().futures.tradePreviews[params.provider]
			dispatch(
				setTradePreview({
					preview: existing,
					provider: params.provider,
				})
			)
			dispatch(setQueryStatus({ key: queryKey, status: FetchStatus.Success }))
			return
		}

		if ((!params.orderPrice || params.sizeDelta.eq(0)) && params.isConditional) {
			// Clear trade preview if waiting for price
			dispatch(
				setTradePreview({
					preview: undefined,
					provider: params.provider,
				})
			)
			return
		}

		if (params.provider === PerpsProvider.SNX_V2_OP) {
			const isolatedPosition = position as PerpsV2Position<string> | undefined
			const freeMargin = selectIsolatedAvailableMargin(getState())
			const marketMargin = wei(isolatedPosition?.details.margin.remainingMargin ?? 0)

			if (
				// Require both size and margin for a trade
				(params.action === 'trade' && (params.sizeDelta.eq(0) || params.marginDelta.eq(0))) ||
				// Require one or the other when editing a position
				(params.sizeDelta.eq(0) && params.marginDelta.eq(0))
			) {
				dispatch(
					setTradePreview({
						preview: undefined,
						provider: PerpsProvider.SNX_V2_OP,
					})
				)
				return
			}

			try {
				// If this is a trade with no existsing position size then we need to subtract
				// remaining idle market margin to get an accurate preview
				const marginDelta =
					wei(isolatedPosition?.details.size ?? 0)
						.abs()
						.eq(0) &&
					marketMargin.gt(0) &&
					params.action === 'trade'
						? params.marginDelta.sub(marketMargin)
						: params.marginDelta

				dispatch(setQueryStatus({ key: queryKey, status: FetchStatus.Loading }))
				const snxChain = selectSnxPerpsV2Network(getState())
				const leverageSide = params.sizeDelta.gt(0) ? PositionSide.LONG : PositionSide.SHORT

				const preview = await sdk.snxPerpsV2.getSmartMarginTradePreview({
					account: (params.accountId || zeroAddress) as Address,
					market: params.market,
					chainId: snxChain,
					tradeParams: {
						...params,
						leverageSide,
						marginDelta,
						orderPrice,
					},
				})

				if (params.marginDelta.gt(freeMargin) && preview.status === 0) {
					// Show insufficient margin message
					preview.status = PotentialTradeStatus.INSUFFICIENT_FREE_MARGIN
					preview.statusMessage = getTradeStatusMessage(
						PotentialTradeStatus.INSUFFICIENT_FREE_MARGIN,
						params.provider
					)
					preview.showStatus = true
				}

				const fees =
					params.market.provider === PerpsProvider.SNX_V2_OP
						? calculateSnxV2TradeFees({
								...params,
								market: {
									marketSkew: wei(params.market.marketSkew),
									feeRates: {
										makerFee: wei(params.market.feeRates.makerFee),
										takerFee: wei(params.market.feeRates.takerFee),
									},
								},
								orderPrice: params.orderPrice ?? params.indexPrice,
							})
						: {
								tradeFee: preview.fee as Wei,
							}

				const desiredFillPrice = calculateDesiredFillPrice(
					params.sizeDelta,
					preview.fillPrice,
					slippage
				)

				const serializedPreview = serializeIsolatedMarginTradePreview({
					...preview,
					...fees,
					desiredFillPrice,
					action: params.action,
					provider: params.provider,
					marginType: FuturesMarginType.ISOLATED_MARGIN,
					leverage: preview.leverage.toNumber(),
					settlementFee: wei(params.market.settlementFee),
					marketAsset: params.market.asset,
					isConditional: !!params.isConditional,
					orderPrice: orderPrice,
					marginDelta: params.marginDelta,
				})

				const debounceCount = selectPreviewCount(getState())
				if (debounceCount !== params.debounceCount) {
					// This would indicate a new preview is being generated so skip the old one
					return
				}

				dispatch(
					setTradePreview({
						preview: serializedPreview,
						provider: params.provider,
					})
				)
				dispatch(setQueryStatus({ key: queryKey, status: FetchStatus.Success }))
			} catch (err) {
				handleFetchError(dispatch, queryKey, err)
				dispatch(
					handlePreviewError({
						provider: params.provider,
						error: err.message,
						previewType: params.action,
					})
				)
				dispatch(
					setTradePreview({
						preview: undefined,
						provider: params.provider,
					})
				)
			}
		} else if (params.provider === PerpsProvider.SNX_V3_BASE) {
			const market = params.market
			const availableMargin = selectCrossMarginAvailableMargin(getState())
			const { accountId, network } = selectSnxV3AccountContext(getState())
			const reservedMargin = selectReservedMarginForOrders(getState())
			const assetsForPrices = selectCrossMarginAssetsForPrices(getState())

			try {
				if (!market) throw new Error('No market selected')
				dispatch(setQueryStatus({ key: queryKey, status: FetchStatus.Loading }))

				const preview = await sdk.snxPerpsV3.getTradePreview({
					accountId: accountId ? BigInt(accountId) : undefined,
					marketId: market.marketId,
					chainId: network,
					sizeDelta: params.sizeDelta,
					indexPrice: params.indexPrice,
					assetsForPrices: [...new Set([market.asset, ...assetsForPrices])],
				})

				const v3Position = position as PerpsV3Position<string>

				const size =
					(v3Position?.details.side === PositionSide.LONG
						? wei(v3Position?.details.size ?? 0)
						: wei(v3Position?.details.size ?? 0).neg()) ?? wei(0)

				// Price impact on limit orders are only a rough indication as
				// it it is only accurate for the current market price
				const priceImpact = preview.fillPrice.sub(params.marketPrice).div(params.marketPrice).abs()
				const notional = preview.fillPrice.mul(params.sizeDelta).abs()
				const tradeableMargin = wei(availableMargin).sub(reservedMargin)
				const newSize = size.add(params.sizeDelta) ?? params.sizeDelta

				const fillPrice = params.isConditional
					? params.orderPrice ?? params.indexPrice
					: preview.fillPrice

				const desiredFillPrice = calculateDesiredFillPrice(params.sizeDelta, fillPrice, slippage)

				const populatedPreview: SnxPerpsV3TradePreview<string> = {
					action: params.action,
					provider: params.provider,
					marginType: FuturesMarginType.CROSS_MARGIN,
					settlementFee: preview.settlementFee.toString(),
					marketId: market.marketId,
					fee: preview.fee.toString(),
					requiredMargin: preview.requiredMargin.toString(),
					fillPrice: fillPrice.toString(),
					priceImpact: priceImpact.toString(),
					newSize: newSize.toString(),
					liqPrice: '0',
					exceedsPriceProtection: priceImpact
						.mul(100)
						.gt(getDefaultPriceImpact(OrderTypeEnum.MARKET)),
					sizeDelta: params.sizeDelta.toString(),
					leverage: notional.gt(0) ? wei(availableMargin).div(notional).toNumber() : 0,
					notionalValue: notional.toString(),
					side: params.sizeDelta.gt(0) ? PositionSide.LONG : PositionSide.SHORT,
					marketAsset: market.asset,
					isConditional: !!params.isConditional,
					desiredFillPrice: desiredFillPrice.toString(),
					orderPrice: orderPrice.toString(),
					status: preview.requiredMargin.gt(tradeableMargin)
						? tradeableMargin.lt(MIN_MARGIN_AMOUNT_V3)
							? PotentialTradeStatus.INSUFFICIENT_FREE_MARGIN
							: PotentialTradeStatus.MAX_POSITION_SIZE_EXCEEDED
						: PotentialTradeStatus.OK,
				}

				if (accountId) {
					const txRequest = await dispatch(prepareCrossMarginTradeTx(populatedPreview)).unwrap()
					populatedPreview.stagedTx = txRequest
				}

				const debounceCount = selectPreviewCount(getState())
				if (debounceCount !== params.debounceCount) {
					// This would indicate a new preview is being generated so skip the old one
					return
				}

				dispatch(
					setTradePreview({
						preview: populatedPreview,
						provider: params.provider,
					})
				)

				dispatch(setQueryStatus({ key: queryKey, status: FetchStatus.Success }))
			} catch (err) {
				handleFetchError(dispatch, queryKey, err, { notify: true })
			}
		}
	}
)

export const clearTradeInputs = createAsyncThunk<void, void, ThunkConfig>(
	'futures/clearTradeInputs',
	async (_, { dispatch }) => {
		dispatch(clearSmartMarginTradeInputs())
		dispatch(clearCrossMarginTradeInputs())
	}
)

export const setTradeStopLossAction = createAsyncThunk<void, string, ThunkConfig>(
	'futures/setTradeStopLoss',
	async (price, { dispatch }) => {
		dispatch(setTradeStopLoss(price))
	}
)

export const editTradePanelOrderPrice =
	(price: string): AppThunk =>
	(dispatch, getState) => {
		const type = selectPerpsProvider(getState())
		if (providerIsCrossMargin(type)) {
			dispatch(editCrossMarginTradeOrderPrice(price))
		} else {
			dispatch(editSmartMarginTradeOrderPrice(price))
		}
	}

export const editTradeSizeInput =
	(size: string, currencyType: 'usd' | 'native'): AppThunk =>
	(dispatch, getState) => {
		const type = selectPerpsProvider(getState())
		if (providerIsCrossMargin(type)) {
			dispatch(editCrossMarginTradeSize(size, currencyType))
		} else {
			dispatch(editIsolatedMarginTradeSize(size, currencyType))
		}
	}

export const fetchFuturesPositionHistory = createAsyncThunk<void, PerpsProvider[], ThunkConfig>(
	'futures/fetchFuturesPositionHistory',
	async (providers, { getState, dispatch, extra: { sdk } }) => {
		const contexts = selectAccountContexts(getState())

		for (const provider of providers) {
			const { wallet, network, accountId } = contexts[provider]
			if (!wallet || !accountId) continue

			try {
				if (providerIsCrossMargin(provider)) {
					const markets = selectV3Markets(getState())
					const assetsForPrices = selectCrossMarginAssetsForPrices(getState())
					const marginInfo = selectCrossMarginMarginInfo(getState())
					const positions = await sdk.snxPerpsV3.getPositions({
						accountId,
						futuresMarkets: markets.map((m) => ({
							id: BigInt(m.marketId),
							asset: m.asset,
						})),
						assetsForPrices,
						chainId: network as SnxV3NetworkIds,
						marginInfo: {
							availableMargin: wei(marginInfo.availableMargin),
							requiredMaintenanceMargin: wei(marginInfo.requiredMaintenanceMargin),
						},
					})
					dispatch(
						updateAccountData({
							wallet,
							data: {
								network: network,
								provider,
								account: accountId.toString(),
								positionHistory: positions.map(serializePosition),
							},
						})
					)
				} else if (provider === PerpsProvider.SNX_V2_OP) {
					const markets = selectV2Markets(getState())
					const positions = await sdk.snxPerpsV2.getPositions({
						account: accountId,
						futuresMarkets: markets.map((m) => ({
							asset: m.asset,
							address: m.marketAddress,
						})),
						chainId: network as SnxV2NetworkIds,
					})
					dispatch(
						updateAccountData({
							wallet,
							data: {
								provider: PerpsProvider.SNX_V2_OP,
								positionHistory: positions.map(serializePosition),
								network,
								account: accountId,
							},
						})
					)
				}
				dispatch(
					setQueryStatus({
						key: 'get_trader_position_history',
						status: FetchStatus.Success,
						provider,
					})
				)
			} catch (err) {
				handleFetchError(dispatch, 'get_trader_position_history', err, { provider })
			}
		}
	}
)

export const fetchPositionsForTrader = createAsyncThunk<void, void, ThunkConfig>(
	'futures/fetchPositionsForTrader',
	async (_, { getState, dispatch, extra: { sdk } }) => {
		const selectedTrader = selectSelectedTrader(getState())
		const perpsProvider = selectPerpsProvider(getState())
		const chainId = selectProviderNetworks(getState())[perpsProvider]

		if (!selectedTrader) {
			return
		}
		const { trader: traderAddress, accountId: traderAccountId } = selectedTrader
		try {
			dispatch(setQueryStatus({ key: 'get_trader_positions', status: FetchStatus.Loading }))
			if (providerIsCrossMargin(perpsProvider) && traderAccountId) {
				const markets = selectV3Markets(getState())
				const assetsForPrices = selectCrossMarginAssetsForPrices(getState())
				const positions = await sdk.snxPerpsV3.getPositions({
					accountId: traderAccountId,
					futuresMarkets: markets.map((m) => ({
						id: BigInt(m.marketId),
						asset: m.asset,
					})),
					assetsForPrices,
					chainId: chainId as SnxV3NetworkIds,
				})
				dispatch(
					setTraderHistory({
						provider: perpsProvider,
						positions: positions.map(serializePosition),
						traderAddress,
					})
				)
			}
			if (perpsProvider === PerpsProvider.SNX_V2_OP) {
				const markets = selectV2Markets(getState())
				const positions = await sdk.snxPerpsV2.getPositions({
					account: traderAddress,
					futuresMarkets: markets.map((m) => ({
						asset: m.asset,
						address: m.marketAddress,
					})),
					chainId: chainId as SnxV2NetworkIds,
					status: 'all',
					accountType: 'eoa',
				})
				dispatch(
					setTraderHistory({
						provider: PerpsProvider.SNX_V2_OP,
						positions: positions.map(serializePosition),
						traderAddress,
					})
				)
			}
			dispatch(setQueryStatus({ key: 'get_trader_positions', status: FetchStatus.Success }))
		} catch (err) {
			handleFetchError(dispatch, 'get_trader_positions', err)
		}
	}
)

export const fetchTradesForPosition = createAsyncThunk<
	void,
	{ positionId: string; provider: PerpsProvider; asset: string }[],
	ThunkConfig
>('futures/fetchTradesForPosition', async (positions, { dispatch, getState, extra: { sdk } }) => {
	const contexts = selectAccountContexts(getState())
	const accountData = selectAccountData(getState())

	for (const { positionId, provider } of positions) {
		const { wallet, network, accountId } = contexts[provider]
		if (!wallet || !accountId) continue

		dispatch(setQueryStatus({ key: 'get_trades_for_position', status: FetchStatus.Loading }))

		try {
			let trades: FuturesTrade[] = []
			if (provider === PerpsProvider.SNX_V2_OP) {
				trades = await sdk.snxPerpsV2.getTradeHistory({
					positionId,
					chainId: network as SnxV2NetworkIds,
				})
			} else if (providerIsCrossMargin(provider)) {
				trades = await sdk.snxPerpsV3.getTradesForPosition(positionId, network as SnxV3NetworkIds)
			}

			const updatedTradesByPosition = cloneDeep(accountData?.tradesByPosition ?? {})
			updatedTradesByPosition[positionId] = serializeTrades(trades)

			dispatch(
				updateAccountData({
					wallet,
					data: {
						provider,
						tradesByPosition: updatedTradesByPosition,
						network,
						account: accountId,
					},
				})
			)
			dispatch(setQueryStatus({ key: 'get_trades_for_position', status: FetchStatus.Success }))
		} catch (err) {
			handleFetchError(dispatch, 'get_trades_for_position', err, { provider })
		}
	}
})

export const fetchDelegatesForAccount = createAsyncThunk<void, PerpsProvider[], ThunkConfig>(
	'futures/fetchDelegatesForAccount',
	async (providers, { dispatch, getState, extra: { sdk } }) => {
		const contexts = selectAccountContexts(getState())

		for (const provider of providers) {
			const addressBook = selectAddressBook(getState())
			const { wallet, network, accountId } = contexts[provider]

			if (!wallet || !accountId) continue

			let delegates: DelegateChangeInfo[] = []
			try {
				dispatch(setQueryStatus({ key: 'get_delegates', provider, status: FetchStatus.Loading }))

				if (provider === PerpsProvider.SNX_V2_OP) {
					delegates = await sdk.snxPerpsV2.getDelegatesForAccount(
						wallet,
						network as SnxV2NetworkIds
					)
				} else if (providerIsCrossMargin(provider)) {
					delegates = await sdk.snxPerpsV3.getDelegatesForAccount(
						wallet,
						network as SnxV3NetworkIds
					)
				}

				const addressMap = Object.fromEntries(
					addressBook.map((d) => [getAddress(d.address), d.nickname])
				)

				const delegatesWithNicknames = delegates.map(({ delegate }) => {
					const address = getAddress(delegate)
					const nickname = addressMap[address] || 'New Delegate'
					return { address, nickname }
				}) as DelegationAccountInfo[]

				dispatch(
					updateAccountData({
						wallet,
						data: {
							account: accountId,
							provider,
							network,
							delegates: delegatesWithNicknames,
						},
					})
				)
				dispatch(setQueryStatus({ key: 'get_delegates', provider, status: FetchStatus.Success }))
			} catch (err) {
				handleFetchError(dispatch, 'get_delegates', err, { provider })
			}
		}
	}
)

export const fetchSubAccountsForAccount = createAsyncThunk<void, PerpsProvider[], ThunkConfig>(
	'futures/fetchSubAccountsForAccount',
	async (providers, { dispatch, getState, extra: { sdk } }) => {
		const contexts = selectAccountContexts(getState())
		for (const provider of providers) {
			const addressBook = selectAddressBook(getState())
			const wallet = selectSignerWallet(getState())

			if (!wallet) continue

			let subAccounts: DelegateChangeInfo[] = []

			try {
				dispatch(setQueryStatus({ key: 'get_sub_accounts', provider, status: FetchStatus.Loading }))

				if (provider === PerpsProvider.SNX_V2_OP) {
					const { network } = contexts[provider]
					subAccounts = await sdk.snxPerpsV2.getSubAccountsForAccount(
						wallet,
						network as SnxV2NetworkIds
					)
				} else if (providerIsCrossMargin(provider)) {
					const network = selectSnxV3Networks(getState())[provider]
					subAccounts = await sdk.snxPerpsV3.getSubAccountsForAccount(
						wallet,
						network as SnxV3NetworkIds
					)
				}

				const addressMap = Object.fromEntries(
					addressBook.map((d) => [getAddress(d.address), d.nickname])
				)
				const subAccountsWithNicknames = subAccounts.map(({ caller }) => {
					const address = getAddress(caller)
					const nickname = addressMap[address] || 'New Sub-Account'
					return { address, nickname }
				}) as DelegationAccountInfo[]

				dispatch(
					setSubAccountsForWallet({
						provider,
						delegateeWallet: wallet,
						subAccounts: subAccountsWithNicknames,
					})
				)

				dispatch(setQueryStatus({ key: 'get_sub_accounts', status: FetchStatus.Success }))
			} catch (err) {
				handleFetchError(dispatch, 'get_sub_accounts', err)
			}
		}
	}
)

export const editTradeMarginDelta =
	(marginDelta: string): AppThunk =>
	(dispatch, getState) => {
		const { accountId } = selectAccountContext(getState())
		const marketInfo = selectMarketInfo(getState())
		const marketPrice = selectSkewAdjustedPriceInfo(getState())
		const isConditional = selectIsConditionalOrder(getState())
		const orderPriceInput = selectTradePanelOrderPriceInput(getState())
		const orderPrice = selectTradePanelTradePrice(getState())

		const { susdSize, nativeSizeDelta, orderType } = selectTradePanelInputs(getState())

		if (!marketInfo) throw new Error('No market selected')
		if (marketInfo.marginType === FuturesMarginType.CROSS_MARGIN)
			throw new Error('Invalid market type')

		if (!marginDelta || Number(marginDelta) === 0) {
			dispatch(
				setTradeInputs({
					...ZERO_STATE_TRADE_INPUTS,
					orderType,
					orderPrice: { price: orderPriceInput },
				})
			)
			dispatch(setLeverageInput(''))
			dispatch(setMarginDelta(marginDelta))
			dispatch(clearTradePreview())
			return
		}

		const marginDelatWei = wei(marginDelta)
		const leverage = wei(susdSize || 0).div(marginDelatWei.abs())

		dispatch(setMarginDelta(marginDelta))
		if (!leverage.eq(0)) {
			dispatch(setLeverageInput(leverage.toString(2)))
		}

		const tradeParams: IsolatedTradePreviewParams = {
			provider: marketInfo.provider,
			accountId,
			market: marketInfo,
			orderPrice: orderPrice,
			marketPrice: wei(marketPrice?.price ?? 0),
			indexPrice: wei(selectMarketIndexPrice(getState())),
			marginDelta: wei(marginDelta || 0),
			sizeDelta: wei(nativeSizeDelta),
			action: 'trade',
			isConditional,
		}

		dispatch(stageTradePreview(tradeParams))
	}

export const editIsolatedMarginTradeSize =
	(size: string, currencyType: 'usd' | 'native'): AppThunk =>
	(dispatch, getState) => {
		const { accountId } = selectAccountContext(getState())
		const indexPrice = selectMarketIndexPrice(getState())
		const marginDelta = selectIsolatedMarginMarginDelta(getState())
		const orderPrice = selectTradePanelOrderPriceInput(getState())
		const marketPrice = selectSkewAdjustedPriceInfo(getState())
		const isConditional = selectIsConditionalOrder(getState())
		const orderType = selectTradeOrderType(getState())
		const tradeSide = selectLeverageSide(getState())
		const marketInfo = selectMarketInfo(getState())
		const price = selectTradePanelTradePrice(getState())

		if (!marketInfo) throw new Error('No market selected')
		if (marketInfo.marginType !== FuturesMarginType.ISOLATED_MARGIN)
			throw new Error('Invalid market type')

		if (size === '' || !price || price.eq(0)) {
			dispatch(
				setTradeInputs({ ...ZERO_STATE_TRADE_INPUTS, orderType, orderPrice: { price: orderPrice } })
			)
			dispatch(clearTradePreview())
			dispatch(setLeverageInput(''))
			return
		}

		const nativeSize =
			currencyType === 'native' ? size : String(floorNumber(wei(size).div(price), 4))
		const usdSize = currencyType === 'native' ? String(floorNumber(price.mul(size), 4)) : size
		const leverage = marginDelta?.gt(0) ? wei(usdSize).div(marginDelta.abs()) : '0'
		const sizeDeltaWei =
			tradeSide === PositionSide.LONG ? wei(nativeSize || 0) : wei(nativeSize || 0).neg()

		dispatch(
			setTradeInputs({
				orderType,
				susdSize: usdSize,
				nativeSize: nativeSize,
				orderPrice: {
					price: orderPrice,
					invalidLabel: orderPriceInvalidLabel(orderPrice, tradeSide, indexPrice, orderType),
				},
			})
		)
		dispatch(setLeverageInput(leverage.toString(2)))
		dispatch(
			stageTradePreview({
				provider: marketInfo.provider,
				accountId,
				market: marketInfo,
				orderPrice: price,
				indexPrice: wei(selectMarketIndexPrice(getState())),
				marginDelta: wei(marginDelta),
				sizeDelta: sizeDeltaWei,
				action: 'trade',
				isConditional,
				marketPrice: wei(marketPrice?.price ?? 0),
			})
		)
	}
// Contract Mutations

export const createIsolatedMarginAccount = createAsyncThunk<
	void,
	{ provider: PerpsProvider },
	ThunkConfig
>(
	'futures/createIsolatedMarginAccount',
	async ({ provider }, { getState, dispatch, extra: { sdk }, rejectWithValue }) => {
		const wallet = selectWallet(getState())
		const existingAccounts = getState().futures.accounts[provider]
		const abstractionWalletEnabled = selectAbstractionWalletEnabled(getState())

		try {
			if (!wallet) throw new Error('No wallet connected')
			// Already have an account fetched and persisted for this address
			if (existingAccounts?.[wallet]?.account) {
				notifyError('There is already an account associated with this wallet')
				rejectWithValue('Account already created')
			}

			// There are no concept of accounts in Perennial currently but
			// this maps to the pattern used by other providers

			const networkId = selectSnxPerpsV2Network(getState())

			const accounts = await sdk.snxPerpsV2.getSmartMarginAccounts(wallet, networkId)
			// Check for existing account on the contract as only one account per user
			if (accounts[0]) {
				dispatch(
					setAccount({
						account: accounts[0],
						wallet,
						provider: PerpsProvider.SNX_V2_OP,
						networkId,
					})
				)
				return
			}

			dispatch(
				setTransaction({
					chainId: networkId,
					status: TransactionStatus.AwaitingExecution,
					type: 'create_isolated_margin_account',
					hash: null,
				})
			)

			const { request } = await sdk.snxPerpsV2.createSmartMarginAccount(networkId)
			await dispatch(submitTransaction({ request }))
			await dispatch(fetchPerpsAccounts({ providers: [provider] }))

			if (!abstractionWalletEnabled) {
				dispatch(
					setOpenModalWithParams({
						type: 'futures_deposit_withdraw_isolated_margin',
						params: { tab: ManageModalType.Deposit },
					})
				)
			}
		} catch (err) {
			dispatch(handleTransactionError({ message: err.message }))
		}
	}
)

export const submitIsolatedMarginOrder = createAsyncThunk<
	void,
	{ overridePriceProtection: boolean; provider: PerpsProvider },
	ThunkConfig
>(
	'futures/submitIsolatedMarginOrder',
	async ({ overridePriceProtection, provider }, { getState, dispatch, extra: { sdk } }) => {
		const state = getState()
		const marketInfo = selectMarketInfo(state)
		const { wallet, accountId, network } = selectAccountContexts(state)[provider]
		const { nativeSizeDelta: nativeSizeDeltaString } = selectTradePanelInputs(state)
		const orderType = selectTradeOrderType(state)
		const orderPrice = selectTradePanelOrderPriceInput(state)
		const marginDelta = selectIsolatedMarginMarginDelta(state)
		const preview = selectTradePreview(state)
		const position = selectPosition(state)
		const openDelayedOrders = selectIsolatedMarginDelayedOrders(state)
		const { stopLossPrice, takeProfitPrice } = selectTradePanelSlTpInputs(state)

		const nativeSizeDelta = wei(nativeSizeDeltaString)
		try {
			if (!marketInfo) throw new Error('Market info not found')
			if (!accountId) throw new Error('No account found')
			if (!wallet) throw new Error('No wallet connected')
			if (!preview) throw new Error('Missing trade preview')
			if (!overridePriceProtection && preview.exceedsPriceProtection) {
				throw new Error('Price impact exceeds price protection')
			}

			const order: TransactionOrder<string> = {
				marketAsset: marketInfo.asset,
				newSize: wei(preview.newSize).abs().toString(),
				sizeDelta: wei(preview.sizeDelta).abs().toString(),
				type: orderType,
				side: preview.side,
				price: preview.fillPrice,
			}

			dispatch(
				setTransaction({
					chainId: network,
					status: TransactionStatus.AwaitingExecution,
					type: 'submit_isolated_order',
					hash: null,
					order,
				})
			)

			const orderInputs: SmartMarginOrderInputs = {
				sizeDelta: nativeSizeDelta,
				marginDelta: marginDelta,
				desiredFillPrice: wei(preview.desiredFillPrice),
			}

			// To separate Stop Loss and Take Profit from other limit / stop orders
			// we set the size to max big num value.

			const maxSizeDelta = nativeSizeDelta.gt(0) ? SL_TP_MAX_SIZE.neg() : SL_TP_MAX_SIZE

			if (Number(stopLossPrice) > 0) {
				const desiredSLFillPrice = calculateDesiredFillPrice(
					maxSizeDelta,
					wei(stopLossPrice || 0),
					wei(DEFAULT_PRICE_IMPACT_DELTA_PERCENT.STOP_LOSS)
				)
				orderInputs.stopLoss = {
					price: wei(stopLossPrice),
					desiredFillPrice: desiredSLFillPrice,
					sizeDelta: nativeSizeDelta.gt(0) ? SL_TP_MAX_SIZE.neg() : SL_TP_MAX_SIZE,
				}
			}

			if (Number(takeProfitPrice) > 0) {
				const desiredTPFillPrice = calculateDesiredFillPrice(
					maxSizeDelta,
					wei(takeProfitPrice || 0),
					wei(DEFAULT_PRICE_IMPACT_DELTA_PERCENT.TAKE_PROFIT)
				)
				orderInputs.takeProfit = {
					price: wei(takeProfitPrice),
					desiredFillPrice: desiredTPFillPrice,
					sizeDelta: nativeSizeDelta.gt(0) ? SL_TP_MAX_SIZE.neg() : SL_TP_MAX_SIZE,
				}
			}

			if (orderType !== OrderTypeEnum.MARKET) {
				orderInputs.conditionalOrderInputs = {
					orderType,
					price: wei(orderPrice || '0'),
					reduceOnly: false,
				}
			}

			let existingSize = wei(position?.details.size ?? 0)
			existingSize =
				position?.details.side === PositionSide.SHORT ? wei(existingSize).neg() : existingSize
			const isClosing = wei(existingSize).add(nativeSizeDelta).eq(0)

			const staleOrder = openDelayedOrders.find(
				(o) => o.isStale && o.market.asset === marketInfo.asset
			)

			if (marketInfo.provider === PerpsProvider.SNX_V2_OP) {
				const { request } = await sdk.snxPerpsV2.submitSmartMarginOrder({
					market: { marketAddress: marketInfo.marketAddress, asset: marketInfo.asset },
					walletAddress: wallet,
					smAddress: accountId as Address,
					order: orderInputs,
					isOwner: false,
					chainId: network as SnxV2NetworkIds,
					options: {
						cancelPendingReduceOrders: {
							limit: !!orderInputs.stopLoss || (isClosing && orderType === OrderTypeEnum.MARKET),
							stop: !!orderInputs.takeProfit || (isClosing && orderType === OrderTypeEnum.MARKET),
						},
						cancelExpiredDelayedOrders: !!staleOrder,
					},
				})

				const callback = () => {
					dispatch(clearSmartMarginTradeInputs())
					dispatch(setOpenModal(null))
				}

				await dispatch(
					submitFuturesTransaction({
						request,
						onSuccess: callback,
					})
				)
			}

			dispatch(fetchAllOpenOrders([marketInfo.provider]))
			dispatch(setOpenModal(null))
			dispatch(fetchBalancesAndAllowances())
			dispatch(fetchAccountMarginInfo([marketInfo.provider]))
			dispatch(clearSmartMarginTradeInputs())
		} catch (err) {
			logError(err)
			dispatch(handleTransactionError({ message: err.message }))
			throw err
		}
	}
)

export const submitIsolatedLimitOrder = createAsyncThunk<void, boolean, ThunkConfig>(
	'futures/submitIsolatedLimitOrder',
	async (overridePriceProtection, { getState, dispatch, extra: { sdk } }) => {
		const state = getState()

		const provider = selectPerpsProvider(state)
		const { market: marketInfo } = selectEditPositionModalInfo(state)
		const { wallet, accountId, network } = selectAccountContexts(state)[provider]
		const {
			nativeSizeDelta: nativeSizeDeltaString,
			marginDelta: marginDeltaString,
			orderPrice,
		} = selectEditPositionInputs(state)
		const preview = selectTradePreview(state)

		const marginDelta = wei(marginDeltaString || '0')
		const nativeSizeDelta = wei(nativeSizeDeltaString)

		try {
			if (!marketInfo) throw new Error('Market info not found')
			if (!accountId) throw new Error('No account found')
			if (!wallet) throw new Error('No wallet connected')
			if (!preview) throw new Error('Missing trade preview')
			if (!overridePriceProtection && preview.exceedsPriceProtection) {
				throw new Error('Price impact exceeds price protection')
			}

			const order: TransactionOrder<string> = {
				marketAsset: marketInfo.asset,
				newSize: wei(preview.newSize).abs().toString(),
				sizeDelta: wei(preview.sizeDelta).abs().toString(),
				type: OrderTypeEnum.LIMIT,
				side: preview.side,
				price: preview.fillPrice,
			}

			dispatch(
				setTransaction({
					chainId: network,
					status: TransactionStatus.AwaitingExecution,
					type: 'submit_isolated_order',
					hash: null,
					order,
				})
			)

			const orderInputs: SmartMarginOrderInputs = {
				sizeDelta: nativeSizeDelta,
				marginDelta: marginDelta,
				desiredFillPrice: wei(preview.desiredFillPrice),
			}

			orderInputs.conditionalOrderInputs = {
				orderType: OrderTypeEnum.LIMIT,
				price: wei(orderPrice || '0'),
				reduceOnly: false,
			}

			if (marketInfo.provider === PerpsProvider.SNX_V2_OP) {
				const { request } = await sdk.snxPerpsV2.submitSmartMarginOrder({
					market: { marketAddress: marketInfo.marketAddress, asset: marketInfo.asset },
					walletAddress: wallet,
					smAddress: accountId as Address,
					order: orderInputs,
					isOwner: false,
					chainId: network as SnxV2NetworkIds,
				})

				const callback = () => {
					dispatch(setEditPositionInputs({ nativeSizeDelta: '', marginDelta: '', orderPrice: '' }))
					dispatch(setShowEditPositionModal(null))
				}

				await dispatch(
					submitFuturesTransaction({
						request,
						onSuccess: callback,
					})
				)
			}

			dispatch(fetchAllOpenOrders([marketInfo.provider]))
			dispatch(setShowEditPositionModal(null))
			dispatch(fetchBalancesAndAllowances())
			dispatch(fetchAccountMarginInfo([marketInfo.provider]))
			dispatch(clearEditPositionInputs())
		} catch (err) {
			logError(err)
			dispatch(handleTransactionError({ message: err.message }))
			throw err
		}
	}
)

export type CancelCondOrdersSnxV3Params = {
	provider: PerpsProvider.SNX_V3_BASE
	orderIds: number[]
}
export type CancelCondOrdersIsolatedParams = {
	provider: PerpsProvider.SNX_V2_OP
	orderId: number
	marketAsset: FuturesMarketAsset
}

export const cancelConditionalOrder = createAsyncThunk<
	void,
	CancelCondOrdersSnxV3Params | CancelCondOrdersIsolatedParams,
	ThunkConfig
>(
	'futures/cancelConditionalOrder',
	async (params, { getState, dispatch, extra: { sdk, smartAccount } }) => {
		const state = getState()
		const { accountId, network, wallet } = selectAccountContext(state)
		const abstractionWalletEnabled = selectAbstractionWalletEnabled(state)

		try {
			if (!accountId || !wallet) throw new Error('No account data')

			if (params.provider === PerpsProvider.SNX_V3_BASE) {
				const orderCancels = params.orderIds

				await sdk.snxPerpsV3.cancelConditionalOrders(
					orderCancels,
					abstractionWalletEnabled ? smartAccount.getSigner() ?? undefined : undefined
				)
				dispatch(fetchOpenConditionalOrders([params.provider]))
			} else {
				dispatch(
					setTransaction({
						chainId: network,
						status: TransactionStatus.AwaitingExecution,
						type: 'cancel_cross_margin_order',
						hash: null,
					})
				)

				let request: Transaction | undefined = undefined
				if (params.provider === PerpsProvider.SNX_V2_OP) {
					const tx = await sdk.snxPerpsV2.cancelConditionalOrder({
						account: accountId as Address,
						orderId: Number(params.orderId),
						chainId: network as SnxV2NetworkIds,
					})
					request = simulatedRequestToTxRequest(tx.request)
				}

				if (!request) return

				dispatch(setCancellingConditionalOrder((params as CancelCondOrdersIsolatedParams).orderId))
				await dispatch(submitFuturesTransaction({ request }))

				dispatch(setCancellingConditionalOrder(undefined))
				dispatch(setOpenModal(null))
				dispatch(fetchAllOpenOrders([params.provider]))
			}
		} catch (err) {
			logError(err)
			dispatch(setCancellingConditionalOrder(undefined))
			dispatch(handleTransactionError({ message: err.message }))
			throw err
		}
	}
)

export const onboardCrossMargin = createAsyncThunk<void, void, ThunkConfig>(
	'futures/onboardCrossMargin',
	async (_, { getState, dispatch, extra: { sdk, smartAccount } }) => {
		const { provider, network } = selectAccountContext(getState())
		try {
			const signerAddress = selectAccountAbstractionOwnerAddress(getState())
			if (!signerAddress) throw new Error('No signer address')

			dispatch(
				setTransaction({
					chainId: network,
					status: TransactionStatus.AwaitingExecution,
					type: 'onboard_cross_margin',
					hash: null,
				})
			)
			const { result: accountId, request: createAccount } = await sdk.snxPerpsV3.createAccount(
				network as SnxV3NetworkIds
			)

			const delegateToSmartSigner = await sdk.snxPerpsV3.grantDelegatePermission(
				BigInt(accountId),
				signerAddress,
				network as SnxV3NetworkIds
			)

			const grantMarginEnginePermission = await sdk.snxPerpsV3.grantMarginEnginePermission(
				BigInt(accountId),
				network as SnxV3NetworkIds
			)

			const txHash = await smartAccount.submitTransaction({
				simulateTxs: [createAccount, delegateToSmartSigner, grantMarginEnginePermission],
			})

			if (txHash) {
				await monitorAndAwaitTransaction(network, dispatch, txHash)
				dispatch(fetchPerpsAccounts({ providers: [provider], refetch: true }))
			}
		} catch (err) {
			dispatch(handleTransactionError({ message: err.message }))
			throw err
		}
	}
)

export const fetchSavedAddresses = createAsyncThunk<string[], void, ThunkConfig>(
	'futures/fetchSavedAddresses',
	async (_, { getState }) => {
		const authToken = selectAuthToken(getState())
		if (!authToken) throw new Error('No auth token')
		try {
			return await fetchAddresses(authToken)
		} catch (err) {
			logError(err)
			throw new Error(`Failed to fetch saved addresses: ${err.message}`)
		}
	}
)

export const addSavedAddress = createAsyncThunk<string, string, ThunkConfig>(
	'futures/addSavedAddress',
	async (address, { getState }) => {
		const authToken = selectAuthToken(getState())
		if (!authToken) throw new Error('No auth token')
		try {
			return await addAddress(authToken, address)
		} catch (err) {
			logError(err)
			throw new Error(`Failed to add saved address: ${err.message}`)
		}
	}
)

export const removeSavedAddress = createAsyncThunk<string, string, ThunkConfig>(
	'futures/removeSavedAddress',
	async (address, { getState }) => {
		const authToken = selectAuthToken(getState())
		if (!authToken) throw new Error('No auth token')
		try {
			return await removeAddress(authToken, address)
		} catch (err) {
			logError(err)
			throw new Error(`Failed to remove saved address: ${err.message}`)
		}
	}
)

// One-Click Trading
export const delegateToOneClickTrading = createAsyncThunk<void, void, ThunkConfig>(
	'futures/delegateToOneClickTrading',
	async (_, { getState, dispatch, extra: { sdk, accountAbstractionFactory } }) => {
		const { network, provider, wallet, accountId } = selectAccountContext(getState())
		const accountAbstraction = accountAbstractionFactory.getAccountAbstraction(provider)
		const address = accountAbstraction?.accountAddress

		try {
			if (!wallet) throw new Error('No wallet connected')
			if (!address) throw new Error('No abstraction address connected')
			if (!accountId) throw new Error('No account connected')
			dispatch(
				setTransaction({
					chainId: network,
					status: TransactionStatus.AwaitingExecution,
					type: 'delegate_to_smart_margin',
					hash: null,
				})
			)

			let txHash: Hash
			if (provider === PerpsProvider.SNX_V2_OP) {
				const { request } = await sdk.snxPerpsV2.delegateSmartMarginAccount({
					account: accountId as Address,
					delegate: address,
					chainId: network as SnxV2NetworkIds,
				})
				txHash = await sdk.transactions.writeContract(request as WriteContractParameters, network)
			} else if (providerIsCrossMargin(provider)) {
				const request = await sdk.snxPerpsV3.grantDelegatePermission(
					BigInt(accountId),
					address,
					network as SnxV3NetworkIds
				)
				txHash = await sdk.transactions.writeContract(request as WriteContractParameters, network)
			} else {
				throw new Error('Unsupported provider')
			}
			await monitorAndAwaitTransaction(network, dispatch, txHash)
			dispatch(setDelegated(true))
		} catch (err) {
			dispatch(handleTransactionError({ message: err.message }))
			dispatch(fetchAbstractionDelegateStatus())
		}
	}
)

export const startOneClickSession = createAsyncThunk<void, DateInterval | number, ThunkConfig>(
	'futures/delegateToOneClickTrading',
	async (interval, { getState, dispatch, extra: { accountAbstractionFactory } }) => {
		const smAccount = selectSnxV2Account(getState())
		const { provider, network } = selectAccountContext(getState())
		const accountAbstraction = accountAbstractionFactory.getAccountAbstraction(provider)
		const address = accountAbstraction?.accountAddress
		const accountId = selectSnxV3Account(getState())
		// TODO: Change to pass network
		const engineAddress = SNX_V3_PERPS_ADDRESSES.MarginEngine[network as SnxV3NetworkIds]

		if (!address) throw new Error('No abstraction address connected')
		if (!smAccount && provider === PerpsProvider.SNX_V2_OP)
			throw new Error('No smart margin account connected')
		if ((!accountId || !engineAddress) && provider === PerpsProvider.SNX_V3_BASE)
			throw new Error('No cross margin account connected')

		const sessionAddress =
			provider === PerpsProvider.SNX_V3_BASE
				? engineAddress
				: provider === PerpsProvider.SNX_V2_OP
					? smAccount
					: undefined

		try {
			dispatch(
				setTransaction({
					chainId: network,
					status: TransactionStatus.AwaitingExecution,
					type: 'create_session',
					hash: null,
				})
			)

			const session = await accountAbstraction.createSession(interval, sessionAddress!)

			dispatch(setSession(session))
			dispatch(updateTransactionStatus(TransactionStatus.Confirmed))
		} catch (err) {
			logError(err)
			dispatch(handleTransactionError({ message: err.message }))
		}
	}
)

export const closeOneClickSession = createAsyncThunk<void, void, ThunkConfig>(
	'futures/delegateToOneClickTrading',
	async (_, { getState, dispatch, extra: { accountAbstractionFactory } }) => {
		const { provider, network } = selectAccountContext(getState())
		const accountAbstraction = accountAbstractionFactory.getAccountAbstraction(provider)
		const address = accountAbstraction?.accountAddress

		if (!address) throw new Error('No abstraction address connected')

		try {
			dispatch(
				setTransaction({
					chainId: network,
					status: TransactionStatus.AwaitingExecution,
					type: 'close_session',
					hash: null,
				})
			)

			const session = await accountAbstraction.closeAllSessions()

			dispatch(setSession(session))
			dispatch(updateTransactionStatus(TransactionStatus.Confirmed))
		} catch (err) {
			logError(err)
			dispatch(handleTransactionError({ message: err.message }))
		}
	}
)

export const withdrawFundsFromOneClick = createAsyncThunk<
	void,
	{ amount: string; token: AbstractionToken },
	ThunkConfig
>(
	'futures/withdrawFundsFromOneClick',
	async ({ amount, token }, { getState, dispatch, extra: { sdk, accountAbstractionFactory } }) => {
		const { provider, network } = selectAccountContext(getState())
		const accountAbstraction = accountAbstractionFactory.getAccountAbstraction(provider)
		const accountAddress = selectWallet(getState())
		const oneClickTradingWalletAddress = selectOneClickTradingWalletAddress(getState())
		const walletClient = sdk.context.walletClient

		try {
			if (
				!walletClient ||
				!accountAbstraction ||
				!oneClickTradingWalletAddress ||
				!accountAddress ||
				!network
			)
				throw new Error('No account connected')

			dispatch(
				setTransaction({
					chainId: network,
					status: TransactionStatus.AwaitingExecution,
					type: 'withdraw_one_click',
					hash: null,
				})
			)

			if (token === AbstractionToken.ETH) {
				const tx = {
					to: accountAddress,
					value: parseEther(amount),
				}

				const res = await accountAbstraction.sendTransactions({
					readyTxs: [tx],
					options: {},
				})

				if (typeof res === 'string' && isHash(res)) {
					await monitorAndAwaitTransaction(network, dispatch, res)
				} else {
					await monitorAndAwaitUserOp(dispatch, res)
				}
				dispatch(fetchBalancesAndAllowances())
			} else if (token === AbstractionToken.USDC) {
				const tokenAddr = TOKEN_ADDRESSES[AbstractionToken.USDC][network]
				if (!tokenAddr) throw Error('Unsupported Signer Network')
				const request = await sdk.tokens.transferToken({
					address: tokenAddr,
					to: accountAddress,
					amount: parseUnits(amount, tokenDecimals('USDC', network)),
					signer: oneClickTradingWalletAddress,
					chainId: network,
				})

				const tx = await accountAbstraction.sendTransactions({
					simulateTxs: [request],
					options: {},
				})

				if (typeof tx === 'string' && isHash(tx)) {
					await monitorAndAwaitTransaction(network, dispatch, tx)
				} else {
					await monitorAndAwaitUserOp(dispatch, tx)
				}
			}
		} catch (err) {
			dispatch(handleTransactionError({ message: err.message }))
		}
	}
)

export const initAccountAbstraction = createAsyncThunk<void, WalletClient | undefined, ThunkConfig>(
	'futures/initAccountAbstraction',
	async (client, { dispatch, getState, extra: { sdk, accountAbstractionFactory } }) => {
		const { provider, network } = selectAccountContext(getState())
		const accountAbstraction = accountAbstractionFactory.getAccountAbstraction(provider)
		const publicClient = sdk.context.clients[network]

		if (!accountAbstraction || !publicClient) return
		if (client) {
			await accountAbstraction.init(client, publicClient)

			const { accountAddress } = accountAbstraction

			if (!accountAddress) return

			const session = await accountAbstraction.getSessionInfo()

			dispatch(setSession(session))
			dispatch(setOneClickTradingWalletAddress(accountAddress))
		} else {
			accountAbstraction.disconnect()
		}
	}
)

export const fetchAbstractionDelegateStatus = createAsyncThunk<void, void, ThunkConfig>(
	'futures/fetchAbstractionDelegateStatus',
	async (_, { getState, dispatch, extra: { sdk } }) => {
		const oneClickTradingWalletAddress = selectOneClickTradingWalletAddress(getState())
		const smAddress = selectSnxV2Account(getState())
		const accountId = selectSnxV3Account(getState())

		const { wallet, network, provider } = selectAccountContext(getState())

		if (!wallet) throw new Error('No wallet connected')
		if (!oneClickTradingWalletAddress)
			throw new Error('No one click trading wallet address connected')
		if (!smAddress && provider === PerpsProvider.SNX_V2_OP)
			throw new Error('No smart margin account connected')
		if (!accountId && provider === PerpsProvider.SNX_V3_BASE)
			throw new Error('No cross margin account connected')

		let isDelegate = false

		if (provider === PerpsProvider.SNX_V2_OP) {
			isDelegate = await sdk.snxPerpsV2.checkDelegateSmartMarginAccount({
				account: smAddress!,
				delegate: oneClickTradingWalletAddress,
				chainId: network as SnxV2NetworkIds,
			})
		} else if (providerIsCrossMargin(provider)) {
			isDelegate = await sdk.snxPerpsV3.checkDelegatePermission(
				accountId!,
				oneClickTradingWalletAddress,
				network as SnxV3NetworkIds
			)
		}

		dispatch(setDelegated(isDelegate))
	}
)

export const handleOneClickOperation = createAsyncThunk<void, VoidFunction, ThunkConfig>(
	'futures/handleOneClickOperation',
	async (callback, { getState, dispatch }) => {
		const tradingMode = selectTradingMode(getState())
		const isSessionActive = selectSessionExpiry(getState()) > Math.floor(Date.now() / 1000)
		const isDelegated = selectAbstractionDelegated(getState())

		const isNeedActivate = !isSessionActive && tradingMode === TradingModes.ONE_CLICK

		try {
			if (IS_ONE_CLICK_TRADING_ENABLED) {
				if (isNeedActivate) {
					if (isDelegated) {
						await dispatch(startOneClickSession('1D'))
					} else {
						dispatch(setOpenModal('one_click_trading_onboard'))
						return
					}
				}
			}
			return callback()
		} catch (err) {
			dispatch(handleTransactionError({ message: err.message }))
		}
	}
)

export const editPositionSize =
	(nativeSizeDelta: string): AppThunk =>
	(dispatch, getState) => {
		const type = selectPerpsProvider(getState())
		dispatch(
			setEditPositionInputs({
				marginDelta: '',
				nativeSizeDelta: nativeSizeDelta,
			})
		)
		if (providerIsCrossMargin(type)) {
			dispatch(editCrossMarginPositionSize(nativeSizeDelta))
		} else {
			dispatch(editIsolatedMarginPositionSize(nativeSizeDelta))
		}
	}

export const editPositionOrderPrice =
	(price: string): AppThunk =>
	(dispatch, getState) => {
		const type = selectPerpsProvider(getState())
		dispatch(
			setEditPositionInputs({
				marginDelta: '',
				orderPrice: price,
				orderType: OrderTypeEnum.LIMIT,
			})
		)
		if (providerIsCrossMargin(type)) {
			dispatch(editCrossMarginPositionOrderPrice(price))
		} else {
			dispatch(editIsolatedMarginPositionOrderPrice(price))
		}
	}

export const editClosePositionPrice =
	(price: string): AppThunk =>
	(dispatch, getState) => {
		const type = selectPerpsProvider(getState())
		if (providerIsCrossMargin(type)) {
			dispatch(editCloseCMPositionPrice(price))
		} else {
			dispatch(editCloseIsolatedPositionPrice(price))
		}
	}

export const editClosePositionSizeDelta =
	(sizeDelta: string): AppThunk =>
	(dispatch, getState) => {
		const type = selectPerpsProvider(getState())
		if (providerIsCrossMargin(type)) {
			dispatch(editCrossMarginCloseAmount(sizeDelta))
		} else {
			dispatch(editCloseIsolatedPositionSizeDelta(sizeDelta))
		}
	}

export const changeLeverageSide =
	(side: PositionSide): AppThunk =>
	(dispatch) => {
		dispatch(setLeverageSide(side))
		dispatch(clearTradeInputs())
	}

export const editConditionalOrder =
	(order: ConditionOrderTableItem): AppThunk =>
	(dispatch, getState) => {
		const type = selectPerpsProvider(getState())
		dispatch(
			setEditConditonalOrderInputs({
				orderId: order.id,
				orderPrice: {
					price: order.targetPrice.toString(),
					invalidLabel: undefined,
				},
				size: order.size.toString(),
				margin: order.marginDelta.toString(),
			})
		)
		if (providerIsCrossMargin(type)) {
			dispatch(
				editCrossMarginConditionalOrder(
					order,
					order.size.abs().toString(),
					order.targetPrice.toString()
				)
			)
		} else {
			dispatch(
				editIsolatedMarginConditionalOrder(
					order,
					order.size.abs().toString(),
					order.targetPrice.toString(),
					order.marginDelta.toString()
				)
			)
		}
	}

// Delegate
export const addDelegateForAccount = createAsyncThunk<void, void, ThunkConfig>(
	'futures/addDelegateForAccount',
	async (_, { getState, dispatch, extra: { sdk, smartAccount } }) => {
		const state = getState()
		const { accountId, wallet, network, provider } = selectAccountContext(state)
		const delegatedAddress = selectSelectedDelegationAddress(state)
		const newNickname = selectNewNickname(state)
		const abstractionWalletEnabled = selectAbstractionWalletEnabled(state)
		if (!accountId) {
			notifyError('No account connected')
			return
		}

		if (!wallet) {
			notifyError('No wallet connected')
			return
		}

		if (!delegatedAddress) {
			notifyError('No delegate address provided')
			return
		}

		try {
			dispatch(
				setTransaction({
					chainId: network,
					status: TransactionStatus.AwaitingExecution,
					type: 'add_delegate',
					hash: null,
				})
			)

			let txHash: Hash | undefined
			switch (provider) {
				case PerpsProvider.SNX_V3_BASE: {
					const request = await sdk.snxPerpsV3.grantDelegatePermission(
						BigInt(accountId),
						delegatedAddress,
						network as SnxV3NetworkIds
					)
					if (abstractionWalletEnabled) {
						txHash = await smartAccount.submitTransaction({
							simulateTxs: [request],
						})
					} else {
						txHash = await sdk.transactions.writeContract(
							request as WriteContractParameters,
							network as SnxV3NetworkIds
						)
					}
					break
				}
				case PerpsProvider.SNX_V2_OP: {
					const { request } = await sdk.snxPerpsV2.addDelegate({
						account: accountId as Address,
						delegatedAddress,
						chainId: network as SnxV2NetworkIds,
					})

					if (abstractionWalletEnabled) {
						txHash = await smartAccount.submitTransaction({
							simulateTxs: [request],
						})
					} else {
						txHash = await sdk.transactions.writeContract(
							request as WriteContractParameters,
							network as SnxV3NetworkIds
						)
					}
					break
				}
				default:
					notifyError('Unsupported provider')
					return
			}

			if (!txHash) {
				notifyError('Failed to submit transaction')
				return
			}

			await monitorAndAwaitTransaction(network, dispatch, txHash)

			const delegate = { address: delegatedAddress, nickname: newNickname }
			dispatch(addDelegateToAddressBook({ wallet, delegate }))

			const delegates = state.futures.accounts[provider]?.[wallet]?.delegates ?? []

			dispatch(
				updateAccountData({
					wallet,
					data: {
						provider,
						account: accountId.toString(),
						network,
						delegates: [...delegates, { ...delegate }],
					},
				})
			)
			dispatch(setOpenModal(null))
		} catch (err) {
			dispatch(handleTransactionError({ message: err.message }))
			throw err
		}
	}
)

export const removeDelegateFromAccount = createAsyncThunk<void, void, ThunkConfig>(
	'futures/removeDelegateFromAccount',
	async (_, { getState, dispatch, extra: { sdk, smartAccount } }) => {
		const state = getState()
		const { accountId, wallet, network, provider } = selectAccountContext(state)
		const delegatedAddress = selectSelectedDelegationAddress(state)
		const abstractionWalletEnabled = selectAbstractionWalletEnabled(state)
		if (!accountId) {
			notifyError('No account connected')
			return
		}

		if (!wallet) {
			notifyError('No wallet connected')
			return
		}

		if (!delegatedAddress) {
			notifyError('No delegate selected')
			return
		}

		try {
			dispatch(
				setTransaction({
					chainId: network,
					status: TransactionStatus.AwaitingExecution,
					type: 'remove_delegate',
					hash: null,
				})
			)

			let txHash: Hash | undefined
			switch (provider) {
				case PerpsProvider.SNX_V3_BASE: {
					const { request } = await sdk.snxPerpsV3.revokeDelegatePermission(
						BigInt(accountId),
						delegatedAddress,
						network as SnxV3NetworkIds
					)
					if (abstractionWalletEnabled) {
						txHash = await smartAccount.submitTransaction({
							simulateTxs: [request],
						})
					} else {
						txHash = await sdk.transactions.writeContract(
							request as WriteContractParameters,
							network as SnxV3NetworkIds
						)
					}
					break
				}
				case PerpsProvider.SNX_V2_OP: {
					const { request } = await sdk.snxPerpsV2.removeDelegate({
						account: accountId as Address,
						delegatedAddress,
						chainId: network as SnxV2NetworkIds,
					})
					if (abstractionWalletEnabled) {
						txHash = await smartAccount.submitTransaction({
							simulateTxs: [request],
						})
					} else {
						txHash = await sdk.transactions.writeContract(
							request as WriteContractParameters,
							network
						)
					}
					break
				}
				default:
					notifyError('Unsupported provider')
					return
			}

			if (!txHash) {
				notifyError('Failed to submit transaction')
				return
			}

			await monitorAndAwaitTransaction(network, dispatch, txHash)
			dispatch(removeDelegateFromAddressBook({ wallet, delegatedAddress }))

			const delegates = state.futures.accounts[provider]?.[wallet]?.delegates ?? []

			dispatch(
				updateAccountData({
					wallet,
					data: {
						provider,
						account: accountId.toString(),
						network,
						delegates: delegates.filter(
							({ address }) => address.toLowerCase() !== delegatedAddress.toLowerCase()
						),
					},
				})
			)
			dispatch(setOpenModal(null))
		} catch (err) {
			dispatch(handleTransactionError({ message: err.message }))
			throw err
		}
	}
)
